import { computed } from "@ember/object";
import { trustHTML } from "@ember/template";
import { isBlank } from "@ember/utils";
import { tagName } from "@ember-decorators/component";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import AdComponent from "./ad-component";

const adIndex = {
  above_site_header: null,
  topic_list_top: null,
  topic_above_post_stream: null,
  topic_above_suggested: null,
  post_bottom: null,
  topic_list_between: null,
  nested_roots_between: null,
};

@tagName("")
export default class HouseAd extends AdComponent {
  adHtml = "";
  currentAd = null;

  _handleAdClick = (event) => {
    // For house ads, only track clicks on <a> tags
    const link = event.target.closest("a");
    if (link && link.href) {
      this.trackClick();
    }
  };

  @computed
  get colspanAttribute() {
    return this.tagName === "td" ? "5" : null;
  }

  @computed("placement", "showAd")
  get adUnitClass() {
    return this.showAd ? `house-${this.placement}` : "";
  }

  @computed("showToGroups", "showAfterPlacement", "showOnCurrentPage")
  get showAd() {
    return (
      this.showToGroups && this.showAfterPlacement && this.showOnCurrentPage
    );
  }

  @computed("placement", "postNumber", "indexNumber")
  get showAfterPlacement() {
    if (this.placement === "topic-list-between") {
      return this.isNthTopicListItem(
        parseInt(this.site.get("house_creatives.settings.after_nth_topic"), 10)
      );
    }

    if (this.placement === "nested-roots-between") {
      return this.isNthTopicListItem(
        parseInt(this.site.get("house_creatives.settings.after_nth_root"), 10)
      );
    }

    if (this.postNumber) {
      return this.isNthPost(
        parseInt(this.site.get("house_creatives.settings.after_nth_post"), 10)
      );
    }

    return true;
  }

  chooseAdHtml() {
    const houseAds = this.site.get("house_creatives"),
      placement = this.get("placement").replace(/-/g, "_"),
      adNames = this.adsNamesForSlot(placement);

    // filter out ads that should not be shown on the current page
    const filteredAds = adNames.filter((adName) => {
      const ad = houseAds.creatives[adName];
      if (!ad) {
        return false;
      }

      const hasCategoryScope = ad.category_ids?.length > 0;
      const hasRouteScope =
        this.siteSettings.ad_plugin_routes_enabled && ad.routes?.length > 0;

      // Global ad: no scopes
      if (!hasCategoryScope && !hasRouteScope) {
        return true;
      }

      // Scoped ad: match category or route
      const matchesCategory =
        hasCategoryScope && ad.category_ids.includes(this.currentCategoryId);
      const matchesRoute =
        hasRouteScope && ad.routes.includes(this.currentRouteName);
      return matchesCategory || matchesRoute;
    });
    if (filteredAds.length > 0) {
      if (!adIndex[placement]) {
        adIndex[placement] = 0;
      }
      let ad = houseAds.creatives[filteredAds[adIndex[placement]]] || "";
      adIndex[placement] = (adIndex[placement] + 1) % filteredAds.length;
      this.currentAd = ad || null;
      return ad.html;
    }

    this.currentAd = null;
  }

  adsNamesForSlot(placement) {
    const houseAds = this.site.get("house_creatives");

    if (!houseAds || !houseAds.settings) {
      return [];
    }

    const adsForSlot = houseAds.settings[placement];

    if (Object.keys(houseAds.creatives).length > 0 && !isBlank(adsForSlot)) {
      return adsForSlot.split("|");
    } else {
      return [];
    }
  }

  refreshAd() {
    this.set("adHtml", this.chooseAdHtml());
  }

  buildImpressionPayload() {
    return {
      ad_plugin_impression: {
        ad_type: this.site.ad_types.house,
        ad_plugin_house_ad_id: this.currentAd?.id,
        placement: this.placement,
      },
    };
  }

  didInsertElement() {
    super.didInsertElement(...arguments);

    if (!this.get("showAd")) {
      return;
    }

    if (adIndex.topic_list_top === null) {
      // start at a random spot in the ad inventory
      const houseAds = this.site.get("house_creatives");
      Object.keys(adIndex).forEach((placement) => {
        const adNames = this.adsNamesForSlot(placement);
        if (adNames.length === 0) {
          return;
        }
        // filter out ads that should not be shown on the current page
        const filteredAds = adNames.filter((adName) => {
          const ad = houseAds.creatives[adName];
          if (!ad) {
            return false;
          }

          const hasCategoryScope = ad.category_ids?.length > 0;
          const hasRouteScope =
            this.siteSettings.ad_plugin_routes_enabled && ad.routes?.length > 0;

          if (!hasCategoryScope && !hasRouteScope) {
            return true;
          }

          const matchesCategory =
            hasCategoryScope &&
            ad.category_ids.includes(this.currentCategoryId);
          const matchesRoute =
            hasRouteScope && ad.routes.includes(this.currentRouteName);
          return matchesCategory || matchesRoute;
        });
        adIndex[placement] = Math.floor(Math.random() * filteredAds.length);
      });
    }

    this.refreshAd();
  }

  <template>
    <div class={{dConcatClass "house-creative" this.adUnitClass}} ...attributes>
      {{#if this.showAd}}
        {{trustHTML this.adHtml}}
      {{/if}}
    </div>
  </template>
}
